import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, CreditCard, Loader2, ShoppingBag } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// International dial codes — Italy first (default). The prefix is mandatory.
export const DIAL_CODES = ["+39", "+44", "+49", "+33", "+34", "+48", "+20", "+41", "+31", "+1"];

export const CartModal = () => {
  const { t, lang } = useLanguage();
  const { items, removeItem, clear, cartOpen, setCartOpen, rawTotal, finalTotal, hasDiscount } = useCart();
  const [name, setName] = useState("");
  const [dial, setDial] = useState("+39");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const resetForm = () => {
    setName(""); setDial("+39"); setPhone("");
  };

  const close = () => { setCartOpen(false); setError(""); };

  const phoneDigits = phone.replace(/[^0-9]/g, "");
  const phoneValid = /^[0-9]{6,14}$/.test(phoneDigits);

  // Direct checkout: create the order, generate its payment link and send the
  // customer straight to the shipping + payment page. The store receives the
  // WhatsApp notification from the backend on order creation.
  const handleCheckout = async () => {
    if (!name.trim()) { setError(t("cart.required")); return; }
    if (!phoneValid) { setError(t("cart.phoneInvalid")); return; }
    setError("");
    setSending(true);
    const orderPayload = {
      customer_name: name.trim(),
      customer_phone: `${dial}${phoneDigits}`,
      lang,
      items: items.map((it) => ({
        category: it.category,
        jersey: it.jersey,
        size: it.size,
        quantity: it.quantity,
        personalization: it.personalization || null,
        patch: it.patch || "none",
        patch_label: it.patchLabel || null,
        notes: it.notes || null,
        price: it.price,
      })),
    };
    try {
      const { data: order } = await axios.post(`${API}/orders`, orderPayload);
      const { data: checkout } = await axios.post(`${API}/orders/${order.id}/checkout`);
      window.location.href = checkout.branded_url;
    } catch (err) {
      console.error("Checkout failed:", err?.response?.data || err.message);
      setSending(false);
      setError(t("cart.checkoutError"));
    }
  };

  const labelCls = "block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body";
  const inputCls = "w-full px-4 py-3.5 bg-transparent border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-500";

  return (
    <AnimatePresence>
      {cartOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={close}
          data-testid="cart-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-sm p-8 max-h-[90vh] overflow-y-auto"
            data-testid="cart-modal"
          >
            <button onClick={close} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors" data-testid="cart-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>

            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white tracking-wide mb-6">
              {t("cart.title")}
            </h3>

            {items.length === 0 ? (
              <p className="text-zinc-500 font-body py-8 text-center" data-testid="cart-empty">{t("cart.empty")}</p>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  {items.map((it) => (
                    <div key={it.id} className="flex items-start justify-between gap-3 p-4 border border-white/5 rounded-sm bg-[#050505]" data-testid="cart-item">
                      <div>
                        <p className="font-heading text-white tracking-wide">{it.jersey}</p>
                        <p className="text-zinc-500 font-body text-xs mt-1">
                          {it.categoryLabel} · {t("order.size")} {it.size} · x{it.quantity}
                          {it.personalization ? ` · ${it.personalization}` : ""}
                          {it.patchLabel ? ` · ${it.patchLabel}` : ""}
                          {it.notes ? ` · ${it.notes}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-[#009A49] font-heading text-lg">€{it.price * it.quantity}</span>
                        <button onClick={() => removeItem(it.id)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid="cart-remove" aria-label={t("cart.remove")}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="flex items-center justify-between px-4 py-3 border border-[#009A49]/30 rounded-sm bg-[#009A49]/5 mb-6">
                  <span className="font-body text-sm uppercase tracking-wider text-zinc-400">{t("cart.total")}</span>
                  <div className="text-right">
                    {hasDiscount ? (
                      <div className="flex items-center gap-2 justify-end">
                        <span className="line-through text-zinc-600 font-body text-sm">€{rawTotal}</span>
                        <span className="text-[#009A49] font-heading text-2xl font-bold" data-testid="cart-total-value">€{finalTotal}</span>
                      </div>
                    ) : (
                      <span className="text-white font-heading text-2xl font-bold" data-testid="cart-total-value">€{finalTotal}</span>
                    )}
                    {hasDiscount && <p className="text-[#009A49] font-body text-xs mt-0.5">{t("cart.discount10")}</p>}
                  </div>
                </div>

                {/* Customer details — shipping is collected on the payment page */}
                <h4 className="font-heading text-white/90 tracking-wider uppercase text-sm mb-4 pt-2 border-t border-white/5" data-testid="cart-details-title">
                  {t("cart.detailsTitle")}
                </h4>

                {/* Name */}
                <div className="mb-5">
                  <label className={labelCls}>{t("cart.name")}</label>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("cart.namePh")}
                    className={inputCls}
                    data-testid="cart-name-input"
                  />
                </div>

                {/* Phone with mandatory international prefix */}
                <div className="mb-5">
                  <label className={labelCls}>{t("cart.phone")}</label>
                  <div className="flex gap-3">
                    <select
                      value={dial}
                      onChange={(e) => setDial(e.target.value)}
                      className="px-3 py-3.5 bg-[#0A0A0A] border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors flex-shrink-0"
                      data-testid="cart-dial-select"
                      aria-label="Prefisso internazionale"
                    >
                      {DIAL_CODES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <input
                      type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("cart.phonePh")}
                      className={inputCls}
                      data-testid="cart-phone-input"
                    />
                  </div>
                </div>

                {error && <p className="text-red-400 font-body text-sm mb-3" data-testid="cart-error">{error}</p>}

                <button onClick={handleCheckout} disabled={sending}
                  className="btn-emerald w-full py-4 text-base font-semibold tracking-wider uppercase rounded-sm flex items-center justify-center gap-3 disabled:opacity-90"
                  data-testid="cart-send-btn"
                >
                  {sending ? (<><Loader2 className="w-5 h-5 animate-spin" />{t("cart.processing")}</>) : (<><CreditCard className="w-5 h-5" />{t("cart.payNow")}</>)}
                </button>

                <button onClick={() => { if (window.confirm(t("cart.confirmClear"))) { clear(); resetForm(); } }}
                  className="w-full mt-3 py-3 text-sm font-body tracking-wider uppercase text-zinc-500 hover:text-red-400 transition-colors flex items-center justify-center gap-2"
                  data-testid="cart-clear-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  {t("cart.clear")}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const FloatingCart = () => {
  const { totalQuantity, setCartOpen } = useCart();
  if (totalQuantity === 0) return null;
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
      onClick={() => setCartOpen(true)}
      className="fixed bottom-24 right-6 z-[90] flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-lg"
      data-testid="floating-cart" aria-label="Open order"
    >
      <ShoppingBag className="w-6 h-6 text-black" />
      <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 flex items-center justify-center rounded-full bg-[#009A49] text-white text-xs font-bold" data-testid="cart-count">
        {totalQuantity}
      </span>
    </motion.button>
  );
};
